import { router, usePage } from "@inertiajs/vue3";
import route from "ziggy-js";
import { create } from "@/composables/useNotifications";
import { useTranslations } from "@/composables/useTranslations";
import { ref } from "vue";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps";

export interface ParkableDomain {
    domain: string;
    parked?: boolean | null;
}

interface ParkDomainToggleOptions {
    onSuccess?: () => void;
    onError?: (errors: Record<string, string>) => void;
}

interface ParkDomainToggleWithLimitGuardOptions extends ParkDomainToggleOptions {
    unparkedDomainsCount: number;
}

export const useDomainParking = () => {
    const { t } = useTranslations();
    const page = usePage<IRhizomPageProps>();
    const showDomainLimitModal = ref(false);
    const domainLimitMessage = ref("");

    const parkDomainToggle = (
        domain: ParkableDomain,
        options: ParkDomainToggleOptions = {},
    ) => {
        router.post(
            route("domains.set-domain-parked", {
                domain: domain.domain,
                toggleParked: !(domain.parked ?? false),
            }),
            {},
            {
                preserveScroll: true,
                preserveState: true,
                onSuccess: () => {
                    create({
                        title: t("vue.domain_parked"),
                        text: t("vue.domain_parked_successfully"),
                        type: "success",
                    });
                    options.onSuccess?.();
                },
                onError: (errors) => {
                    create({
                        title: t("vue.error"),
                        text: getErrorMessage(
                            errors,
                            t("vue.error_parking_domain"),
                        ),
                        type: "error",
                    });
                    options.onError?.(errors);
                },
            },
        );
    };

    const closeDomainLimitModal = () => {
        showDomainLimitModal.value = false;
        domainLimitMessage.value = "";
    };

    const parkDomainToggleWithBasicLimitGuard = (
        domain: ParkableDomain,
        options: ParkDomainToggleWithLimitGuardOptions,
    ) => {
        if (
            shouldBlockUnparkOnSingleDomainPlan(
                domain,
                options.unparkedDomainsCount,
            )
        ) {
            domainLimitMessage.value = t(
                "vue.cannot_add_more_domains_scale_plan",
            );
            showDomainLimitModal.value = true;
            return;
        }

        parkDomainToggle(domain, options);
    };

    return {
        parkDomainToggle,
        parkDomainToggleWithBasicLimitGuard,
        showDomainLimitModal,
        domainLimitMessage,
        closeDomainLimitModal,
    };

    function shouldBlockUnparkOnSingleDomainPlan(
        domain: ParkableDomain,
        unparkedDomainsCount: number,
    ): boolean {
        if (page.props.hasGodMode) {
            return false;
        }

        if (!(page.props.hasFreePlan && !page.props.hasScalePlan)) {
            return false;
        }

        const isUnparkAction = domain.parked === true;

        if (!isUnparkAction) {
            return false;
        }

        return unparkedDomainsCount >= 1;
    }
};

function getErrorMessage(
    errors: Record<string, string>,
    fallback: string,
): string {
    const firstError = Object.values(errors)[0];

    return firstError ?? fallback;
}
